import React, { useRef, useState } from "react";
import "./BeforeAfter.css";

/**
 * BeforeAfter — a drag-to-compare slider (Google-Maps-time-machine feel).
 * Works with mouse, touch and keyboard (arrow keys on the handle).
 * Used for pickup before/after today; ready for neighborhood then/now photos
 * whenever real archival + current shots exist (never AI-invented history).
 */

type Props = {
  before: string;
  after: string;
  beforeLabel: string;
  afterLabel: string;
  alt: string;
  caption?: string;
};

const BeforeAfter: React.FC<Props> = ({ before, after, beforeLabel, afterLabel, alt, caption }) => {
  const [pos, setPos] = useState(58); // % revealed of the "after" side
  const boxRef = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);

  const setFromClientX = (clientX: number) => {
    const box = boxRef.current;
    if (!box) return;
    const r = box.getBoundingClientRect();
    const pct = ((clientX - r.left) / r.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  };

  return (
    <figure className="ba">
      <div
        ref={boxRef}
        className="ba-box"
        onPointerDown={(e) => { dragging.current = true; (e.target as HTMLElement).setPointerCapture?.(e.pointerId); setFromClientX(e.clientX); }}
        onPointerMove={(e) => { if (dragging.current) setFromClientX(e.clientX); }}
        onPointerUp={() => { dragging.current = false; }}
        onPointerCancel={() => { dragging.current = false; }}
      >
        <img className="ba-img ba-before" src={before} alt={alt} draggable={false} />
        <div className="ba-after-wrap" style={{ width: `${pos}%` }}>
          <img className="ba-img ba-after" src={after} alt="" draggable={false} style={{ width: boxRef.current ? boxRef.current.clientWidth : undefined }} />
        </div>
        <span className="ba-tag ba-tag-before">{beforeLabel}</span>
        <span className="ba-tag ba-tag-after">{afterLabel}</span>
        <div className="ba-handle" style={{ left: `${pos}%` }}>
          <input
            type="range"
            min={0}
            max={100}
            value={Math.round(pos)}
            onChange={(e) => setPos(Number(e.target.value))}
            aria-label={`${beforeLabel} / ${afterLabel}`}
            className="ba-range"
          />
          <span className="ba-knob" aria-hidden="true">⇔</span>
        </div>
      </div>
      {caption && <figcaption>{caption}</figcaption>}
    </figure>
  );
};

export default BeforeAfter;
